import {
  formatCityDisplayName,
  formatLocationDisplay,
  getLocationFilterSlug,
  isSamePlaceName,
} from './userLocationDisplay';

/** Known cities/regions for guest concierge (extend as needed). */
const KNOWN_LOCATIONS = [
  'accra',
  'kumasi',
  'tema',
  'tamale',
  'cape coast',
  'takoradi',
  'lagos',
  'abuja',
  'nairobi',
  'mombasa',
  'johannesburg',
  'cape town',
  'new york',
  'los angeles',
  'london',
  'houston',
  'dallas',
  'atlanta',
  'chicago',
  'miami',
];

const SERVICE_PATTERNS = [
  { pattern: /wedding\s+photograph|photograph\w*\s+for\s+(?:a\s+)?wedding/i, service_type: 'photography', category: 'Photography', event_type: 'wedding' },
  { pattern: /wedding\s+photo|photo\s+graph/i, service_type: 'photography', category: 'Photography', event_type: 'wedding' },
  { pattern: /\bphotograph\w*\b/i, service_type: 'photography', category: 'Photography' },
  { pattern: /photo\s*booth|photobooth/i, service_type: 'photo booth', category: 'Photography' },
  { pattern: /\bvideograph\w*\b/i, service_type: 'videography', category: 'Videography' },
  { pattern: /\bdj\b|disc\s*jockey/i, service_type: 'dj', category: 'DJ' },
  { pattern: /event\s*plan|wedding\s*plan|\bplanner\b|\bcoordinator\b/i, service_type: 'planner', category: 'Event Planning' },
  { pattern: /\bvenue\b|\breception\s+hall\b/i, service_type: 'venue', category: 'Venue' },
  { pattern: /\bcater(ing|er)\b/i, service_type: 'catering', category: 'Catering' },
  { pattern: /\bfloral?\b|\bflowers?\b/i, service_type: 'floral', category: 'Floral' },
  { pattern: /\bentertainment\b/i, service_type: 'entertainment', category: 'Entertainment' },
  { pattern: /\btransport/i, service_type: 'transportation', category: 'Transportation' },
];

const EVENT_PATTERNS = [
  { pattern: /\bwedding\b/i, event_type: 'wedding' },
  { pattern: /\bcorporate\b|\bcompany\b|\boffice\b/i, event_type: 'corporate' },
  { pattern: /\bbirthday\b/i, event_type: 'birthday' },
  { pattern: /\bgraduation\b/i, event_type: 'graduation' },
  { pattern: /\bbaby\s*shower\b/i, event_type: 'baby shower' },
];

const extractCity = (lower) => {
  for (const city of KNOWN_LOCATIONS) {
    if (lower.includes(city)) {
      return city;
    }
  }

  const atMatch = lower.match(
    /\b(?:in|at|near|around|within)\s+([a-z][a-z\s-]{2,28}?)(?:\s+(?:for|with|on|this|next|please|area)|[,.!?]|$)/
  );
  if (atMatch?.[1]) {
    return atMatch[1].trim();
  }

  const needMatch = lower.match(
    /\b(?:services?|vendors?|photograph\w*|booth|dj|planner|venue)\s+(?:in|at|near)\s+([a-z][a-z\s-]{2,28}?)(?:[,.!?]|$)/
  );
  if (needMatch?.[1]) {
    return needMatch[1].trim();
  }

  return null;
};

export const getCitySlug = (value) =>
  value ? String(value).trim().toLowerCase().replace(/\s+/g, ' ') : '';

/** Resolve city for concierge shortcuts (city + country label for UI). */
export const resolveGuestCityFromLocation = (userLocation) => {
  if (!userLocation) return null;

  const city = userLocation.city?.trim();
  const country = userLocation.country?.trim();

  if (!city || isSamePlaceName(city, country)) {
    return null;
  }

  const slug = getCitySlug(city);
  const display = formatLocationDisplay({ city, country: country || null });

  return {
    slug,
    display: display || formatCityDisplayName(city),
    filterSlug: getLocationFilterSlug({ city }) || slug,
  };
};

/** Quick prompts + welcome actions based on detected user location. */
export const buildGuestConciergeLocationOptions = (userLocation) => {
  const resolved = resolveGuestCityFromLocation(userLocation);

  if (!resolved?.slug) {
    return {
      citySlug: null,
      cityDisplay: null,
      welcomeActions: [
        { label: 'Browse all vendors', path: '/browse-vendors', type: 'link' },
        { label: 'Create free account', path: '/register', type: 'link' },
      ],
      quickPrompts: ['Find vendors near me', 'Photo booth for a wedding', 'How does booking work?'],
      exampleQuery: 'wedding photography in your city',
      servicesPrompt: 'Find vendors near me',
    };
  }

  const { slug, display, filterSlug } = resolved;
  const cityLabel = formatCityDisplayName(userLocation?.city) || display;
  const regionLabel = userLocation?.display || display;
  const browsePath = `/browse-vendors?city=${encodeURIComponent(filterSlug || slug)}`;

  return {
    citySlug: filterSlug || slug,
    cityDisplay: cityLabel,
    regionDisplay: regionLabel,
    welcomeActions: [
      { label: `Services in ${cityLabel}`, path: browsePath, type: 'link' },
      { label: 'Create free account', path: '/register', type: 'link' },
    ],
    quickPrompts: [`Services in ${cityLabel}`, 'Photo booth for a wedding', 'How does booking work?'],
    exampleQuery: `wedding photography in ${regionLabel}`,
    servicesPrompt: `Services in ${cityLabel}`,
  };
};

/**
 * Build structured hints from user message for guest concierge API.
 * @param {string} text
 * @param {Record<string, unknown>} prev
 * @param {string|null} detectedCitySlug
 */
export const inferGuestConciergeHints = (text, prev = {}, detectedCitySlug = null) => {
  const lower = String(text).toLowerCase().trim();
  const next = { ...prev };

  for (const { pattern, event_type } of EVENT_PATTERNS) {
    if (pattern.test(lower)) {
      next.event_type = event_type;
      break;
    }
  }

  for (const row of SERVICE_PATTERNS) {
    if (row.pattern.test(lower)) {
      if (row.service_type) next.service_type = row.service_type;
      if (row.category) next.category = row.category;
      if (row.event_type) next.event_type = row.event_type;
      break;
    }
  }

  const city = extractCity(lower);
  if (city) {
    next.city = city;
  } else if (
    detectedCitySlug &&
    /\b(near me|around me|my area|my location|local vendors?|services? near)\b/i.test(lower)
  ) {
    next.city = detectedCitySlug;
  }

  const budget = lower.match(/\$?\s*(\d{3,5})/);
  if (budget) {
    next.max_budget = Number(budget[1]);
  }

  const keywords = lower
    .replace(/\b(hello|hi|hey|please|need|want|looking for|i am|i'm|services?|vendors?)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (keywords.length > 2) {
    next.keywords = keywords;
  }

  return next;
};

export const buildBrowseVendorsPath = (hints = {}) => {
  const params = new URLSearchParams();
  if (hints.city) params.set('city', hints.city);
  if (hints.service_type) params.set('q', hints.service_type);
  if (hints.category) params.set('category', hints.category);
  const qs = params.toString();
  return qs ? `/browse-vendors?${qs}` : '/browse-vendors';
};
